<script setup lang="ts">
import { Ref, ref, watch, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router'
import { useAuth } from "@/services/user/Auth0UserProvider";
import Api from "@/services/api/ApiServiceFabrick";
import TextField from "@/components/Common/FormTextField.vue";
import FormRow from "@/components/Common/FormRow.vue"
import PermissionForm from "@/components/UserRoleForm/PermissionForm.vue";
import getUserRoleFormResponseType from "@/types/responses/getUserRoleForm";
import upsertUserRoleFormRequestType from "@/types/requests/upsertUserRoleForm";
import getRoleTemplateResponseType from '@/types/responses/getRoleTemplate'
import { officeType, departmentType } from "@/types/requests/userRoleSearchForm";
import permissionType from "@/types/permission"
import OrganizationAutoComplate from '@/components/AutoCompletes/OrganizationAutoComplate.vue';
import UserRoleService from "@/services/models/UserRole";
import SelectRoleChecker from '@/router/selectRoleChecker'
import DepartmentSelectBox from '@/components/UserRoles/DepartmentSelectBox.vue'
import { OPERATION_OFFICE, DEPARTMENT } from "@/services/consts/user_role";
import i18n from '@/lang/i18n';

const { loginWithRedirect } = useAuth()
const router = useRouter()
const windowInnerWidth: Ref<number> = ref(window.innerWidth);

type userRoleType = {
  display_name: string,
  department: departmentType,
  permissions: permissionType[]
}
const userRole = ref({
  display_name: "",
  department: { id: null, operation_office_id: null, name: "" },
  permissions: []
} as userRoleType)

const isLoading: Ref<boolean> = ref(false)
const isEdit: Ref<boolean> = ref(false)
const isActiveSubmitBtn: Ref<boolean> = ref(false)
const isDepartment: Ref<boolean> = ref(false)
const errors:Ref<any> = ref([]);

const selectedOffice = ref({"id":null, "name":"", "type":"", "departments": []} as officeType)
const selectedOfficeableType: Ref<officeType> = ref({"id":null, "name":"", "type":"", "departments": []})
const roleTemplateList = ref({} as getRoleTemplateResponseType[])

const props = defineProps<{
  userRoleId: number | null,
  showFlash: (message: string, type: string, fadeout: boolean) => { value: any },
  updateAuthority: boolean
}>()
const emits = defineEmits(
  [
    'update:modelValue',
    'setUserRoleId',
    'setContentLoading',
  ]
)

onMounted(() => {
  getSelfRole()
  getRoleTemplates()
  emits("setContentLoading", true);
})

watch(() => props.userRoleId, () => {
  if(props.userRoleId) {
    getUserRole(props.userRoleId)
    isEdit.value = true
  }
});

watch(() => selectedOffice.value, (newVal, oldVal) => {
  if(!isEdit.value) userRole.value.department = { id: null, operation_office_id: null, name: "" }
});

const isDisplayConservationButton = computed(() => {
  if (props.userRoleId) return props.updateAuthority
  return true
})

function getSelfRole(){
  const current_role = SelectRoleChecker.getCurrentRole()
  let organization_type = ""
  if(current_role?.organization_type) organization_type = current_role.organization_type
  if(organization_type == DEPARTMENT.type) isDepartment.value = true
}

function getUserRole(id: number) {
  isLoading.value = true
  Api().getUserRoleForm(id).then((data: getUserRoleFormResponseType) => {
    setUserRole(data)
    setOfficeAndDepartment(data)
    isLoading.value = false
  }).catch((error: any) => {
    isLoading.value = false
    return errorHandle(error.response)
  })
}
function getRoleTemplates(){
  Api().getRoleTemplates().then((data: getRoleTemplateResponseType[]) => {
    roleTemplateList.value = data
    emits("setContentLoading", false);
  }).catch((error: any) => {
    return console.log(error.response)
  })
}

function setUserRole(data: getUserRoleFormResponseType) {
  userRole.value.display_name = data.display_name,
  userRole.value.permissions = data.permissions
}
function setOfficeAndDepartment(data: getUserRoleFormResponseType) {
  let type = data.organization_type
  if(data.department_name){
    userRole.value.department = {
      "id":data.organization_id,
      "name":data.department_name,
      "operation_office_id":data.company_id,
    }
    type = OPERATION_OFFICE['type']
  }
  selectedOffice.value = {
    "id":data.company_id,
    "name":data.company_name,
    "type":type,
    "departments": []
  }
}

function submit() {
  const param = makesParam()
  if(props.userRoleId){
    updateUserRole(props.userRoleId, param)
  }else{
    createUserRole(param)
  }
}

function makesParam() {
  let param = {} as upsertUserRoleFormRequestType

  param['id'] = props.userRoleId ? props.userRoleId : null
  param['display_name'] = userRole.value.display_name
  param['office_id'] = selectedOffice.value.id
  param['office_type'] = UserRoleService.converetOfficeType(selectedOffice.value.type)
  param['department_id'] = userRole.value.department.id
  param['permissions'] = userRole.value.permissions

  return param
}

function createUserRole(param:upsertUserRoleFormRequestType) {
  isLoading.value = true
  errors.value = {}
  Api().createUserRoleForm(param).then((data: getUserRoleFormResponseType) => {
    isLoading.value = false
    props.showFlash(i18n.global.t('pages.common.messages.success'), "success", true)
    isEdit.value = true
    emits("setUserRoleId", data.id);
    router.push('/user-roles')
  }).catch((error: any) => {
    isLoading.value = false
    return errorHandle(error.response)
  })
}

function updateUserRole(id: number, param:upsertUserRoleFormRequestType) {
  isLoading.value = true
  errors.value = {}
  Api().updateUserRoleForm(id, param).then((data: getUserRoleFormResponseType) => {
    isLoading.value = false
    props.showFlash(i18n.global.t('pages.common.messages.successUpdate'), "success", true)
    router.push('/user-roles')
  }).catch((error: any) => {
    isLoading.value = false
    return errorHandle(error.response)
  })
}

function errorHandle (res: any) {
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", true)
      router.push('/user-roles')
      break
    case 422:
      errors.value = res.data.errors
      props.showFlash(i18n.global.t('pages.common.messages.failed'), "error", true)
      break
    case 500:{
      let message = i18n.global.t('pages.common.messages.failed')
      if(isEdit.value) message = i18n.global.t('pages.common.messages.failedUpdate')
      props.showFlash(message, "error", true)
      console.log(res)
    }
  }
}

const selectableDepartment = computed(() => {
  if (
    !selectedOffice.value ||
    !selectedOffice.value.departments ||
    !selectedOffice.value.departments.length
  ) return []
  let departments = [] as departmentType[]
  selectedOffice.value.departments.forEach((department) => {
    departments.push({
      id: department.id,
      operation_office_id: department.operation_office_id,
      name: department.name,
    })
  })
  return departments
})

function changeBtnStatus(status: boolean){
  isActiveSubmitBtn.value = status
}

function handleResize () {
  windowInnerWidth.value = window.innerWidth
}
window.addEventListener('resize', handleResize)

</script>
<template>
  <div class="mb-4">
    <TextField
      v-model="userRole.display_name"
      :title="i18n.global.t('pages.userRoles.form.name')"
      :error="errors.display_name"
      :required="true"
      :windowInnerWidth="windowInnerWidth"
    />
    <FormRow
      :title="i18n.global.t('pages.userRoles.form.companyName')"
      :windowInnerWidth="windowInnerWidth"
      :required="true"
    >
      <OrganizationAutoComplate
        class="mb-2"
        v-model="selectedOffice"
        :selectedOfficeableType="selectedOfficeableType"
        :errorMessages="errors.office_id"
        :readonly="isEdit || isDepartment"
        :clearable="!isEdit && !isDepartment"
      />
    </FormRow>
    <FormRow
      :title="i18n.global.t('pages.userRoles.form.departmentName')"
      :windowInnerWidth="windowInnerWidth"
      :required="isDepartment"
    >
      <DepartmentSelectBox
        v-model="userRole.department"
        :organization="selectedOffice"
        :error-messages="errors.department_id"
        :isEdit="isEdit"
        :isDepartment="isDepartment"
        :clearable="!isEdit && !isDepartment"
      />
    </FormRow>

    <PermissionForm
      v-model="userRole.permissions"
      :roleTemplateList="roleTemplateList"
      :selectedOffice="selectedOffice"
      :selectedDepartment="userRole.department"
      :errors="errors.permissions"
      :isEdit="isEdit"
      :windowInnerWidth="windowInnerWidth"
      @changeBtnStatus="changeBtnStatus"
    />
  </div>
  <div
    class="form-footer p-2"
  >
    <router-link :to="{ name: 'UserRoles' }">
      <v-btn class="ma-2 reset-btn">
        {{ i18n.global.t('common.cancelBtn') }}
      </v-btn>
    </router-link>
    <v-btn
      v-if="isDisplayConservationButton"
      class="ma-2 exec-btn"
      @click="submit"
      :loading="isLoading"
      :disabled="!isActiveSubmitBtn"
    >
      {{ i18n.global.t('common.saveBtn') }}
    </v-btn>
  </div>
</template>
